/**
 * Copiar al portapapeles **en la caseta**, que no es un contexto seguro.
 *
 * `navigator.clipboard` solo existe en HTTPS o en localhost, y el portal de
 * Kodica se sirve por HTTP plano contra la IP del servidor de la caseta —eso no
 * es un descuido de despliegue, es el diseño: la reja tiene que seguir
 * funcionando sin internet, sin certificados y sin depender de un dominio—. Ahí
 * `navigator.clipboard` es `undefined`, así que llamarlo lanzaba un error que
 * nadie recogía: el botón de copiar no copiaba **y tampoco avisaba**.
 *
 * De ahí el respaldo con `execCommand('copy')`, que está marcado como obsoleto y
 * es, en esta pantalla, el único que funciona de verdad. Se intenta primero el
 * moderno porque en un despliegue con HTTPS sí estará, y porque el navegador
 * puede quitar el viejo cualquier día.
 *
 * Devuelve si se pudo. Quien llama tiene que **enseñar el fallo**: un botón que
 * dice «copiado» sin haber copiado hace que alguien pegue el tópico anterior en
 * la configuración de un aparato y pase la tarde buscando por qué no llegan sus
 * mensajes.
 */
export async function copiarTexto(texto: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch {
      // Permiso denegado o contexto inseguro pese a existir el objeto: se cae
      // al respaldo en vez de darlo por perdido.
    }
  }

  try {
    const area = document.createElement('textarea');
    area.value = texto;
    // Fuera de la vista pero **dentro** del documento: un elemento con
    // `display:none` no se puede seleccionar, y sin selección no hay copia.
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, texto.length); // iOS ignora `select()` a secas
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
